import { useState, useEffect, useRef, useCallback } from "react";
import { DbUser } from "canbook-data/user/models";
import { authService } from "canbook-data/auth/service";
import { signInWithCustomToken } from "firebase/auth";
import { firebaseAuth } from "canbook-data/firebase/client-app";
import { z } from "zod";
import { useAnalytics } from "canbook-data/analytics/provider";
import { AnalyticsEvent } from "canbook-data/analytics/model";
import { userService } from "canbook-data/user/service";
import { fetchDeviceInfoAndFcmToken } from "canbook-data/util/device-service";
import { COUNTRY_CODES } from "canbook-data/util/country-codes";
import { setCookie } from "cookies-next";
import {
  ERROR_MESSAGE_OTP,
  ERROR_MESSAGE_LOGIN_WITH_EMAIL,
  ERROR_MESSAGE_USER_NOT_FOUND_USE_EMAIL,
  INVALID_OTP,
  INVALID_EMAIL,
  INVALID_PHONE,
  REQUIRED_ERROR_OTP,
  REQUIRED_ERROR_EMAIL,
  REQUIRED_ERROR_PHONE,
  VALIDATION_ERROR_OTP,
} from "@/utils/const";
import {
  executeRecaptcha,
  parsePhoneNumberForInput,
  EmailComponentCallback,
  OtpComponentCallback,
  PhoneComponentCallback,
} from "./hooks/utils";

export const usePhoneState = (
  phoneNumber: string,
  callback: PhoneComponentCallback,
) => {
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const [errors, setErrors] = useState<any[]>([]);

  const { track, trackError } = useAnalytics();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const { digits: initialPhoneDigits, dialCode: initialDialCode } =
    parsePhoneNumberForInput(phoneNumber);
  const [phone, setPhone] = useState<string>(initialPhoneDigits);
  const [isSubmittedOnce, seIsSubmittedOnce] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>(initialDialCode);

  const checkPhoneValidation = useCallback(
    (phone: string) => {
      const country = COUNTRY_CODES.find((c) => c.dialCode === countryCode);
      const minLength = country?.minLength || 1;
      const maxLength = country?.maxLength || 15;

      const phoneSchema = z.object({
        phone: z
          .number({
            required_error: REQUIRED_ERROR_PHONE,
            invalid_type_error: INVALID_PHONE,
          })
          .positive(),
        phone_length: z
          .string()
          .min(minLength, {
            message: `Phone number must contain at least ${minLength} digits`,
          })
          .max(maxLength, {
            message: `Phone number must contain at most ${maxLength} digits`,
          }),
      });

      const response = phoneSchema.safeParse({
        phone: parseInt(phone),
        phone_length: phone,
      });

      if (!response.success) {
        const errArr: any[] = [];
        const { errors: err } = response.error;
        for (let i = 0; i < err.length; i++) {
          errArr.push({ for: err[i].path[0], message: err[i].message });
        }
        setErrors(errArr);
        setIsInProgress(false);
        return errArr;
      }
      setErrors([]);
      return [];
    },
    [countryCode],
  );

  useEffect(() => {
    if (phone) {
      checkPhoneValidation(phone);
    }
  }, [countryCode, phone, checkPhoneValidation]);

  useEffect(() => {
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const { digits, dialCode } = parsePhoneNumberForInput(phoneNumber);
    setPhone(digits);
    setCountryCode(dialCode);
  }, [phoneNumber]);

  const handlePhoneSubmit = async (e: any) => {
    e.preventDefault();
    setIsInProgress(true);
    seIsSubmittedOnce(true);
    track(AnalyticsEvent.loginGetOtPTap);
    const errors = checkPhoneValidation(phone);
    if (errors.length > 0) return;

    try {
      const token = await executeRecaptcha();
      const digitsOnly = phone.replace(/\D/g, "");
      const phoneFull = `${countryCode}${digitsOnly}`;
      const verificationId = await authService.sendOtp(phoneFull, token);
      callback.onSuccess(verificationId, phoneFull);
    } catch (error: any) {
      const errorCode = error.response?.data;
      if (errorCode === "USER_LOGIN_WITH_EMAIL") {
        setApiError(ERROR_MESSAGE_LOGIN_WITH_EMAIL);
      } else if (
        error.response?.status === 404 &&
        errorCode === "USER_NOT_FOUND_USE_EMAIL"
      ) {
        setApiError(ERROR_MESSAGE_USER_NOT_FOUND_USE_EMAIL);
      } else {
        setApiError(error);
      }
      trackError(AnalyticsEvent.loginGetOTPError, error);
      return;
    } finally {
      setIsInProgress(false);
    }
  };

  return {
    phoneInputRef,
    errors,
    apiError,
    isInProgress,
    phone,
    setPhone,
    countryCode,
    setCountryCode,
    setApiError,
    handlePhoneSubmit,
    checkPhoneValidation,
    isSubmittedOnce,
  };
};

export const useEmailState = (
  emailAddress: string,
  callback: EmailComponentCallback,
) => {
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const [errors, setErrors] = useState<any[]>([]);

  const { track, trackError } = useAnalytics();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(emailAddress);
  const [isSubmittedOnce, seIsSubmittedOnce] = useState<boolean>(false);

  const checkEmailValidation = (email: string) => {
    const emailSchema = z.object({
      email: z
        .string({
          required_error: REQUIRED_ERROR_EMAIL,
          invalid_type_error: INVALID_EMAIL,
        })
        .email({ message: INVALID_EMAIL }),
    });

    const response = emailSchema.safeParse({
      email: email.trim(),
    });

    if (!response.success) {
      const errArr: any[] = [];
      const { errors: err } = response.error;
      for (let i = 0; i < err.length; i++) {
        errArr.push({ for: err[i].path[0], message: err[i].message });
      }
      setErrors(errArr);
      setIsInProgress(false);
      return errArr;
    }

    setErrors([]);
    return [];
  };

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (emailAddress !== undefined && emailAddress !== null) {
      setEmail(emailAddress);
    }
  }, [emailAddress]);

  const handleEmailSubmit = async (e: any) => {
    e.preventDefault();
    setIsInProgress(true);
    seIsSubmittedOnce(true);
    track(AnalyticsEvent.loginGetOtPTap);
    const errors = checkEmailValidation(email);
    if (errors.length > 0) return;

    try {
      const token = await executeRecaptcha();
      const verificationId = await authService.sendOtpEmail(
        email.trim(),
        token,
      );
      callback.onSuccess(verificationId, email.trim());
    } catch (error: any) {
      if (
        error.response?.status === 400 &&
        error.response?.data === "email-already-exists"
      ) {
        setApiError(
          "This email is already registered. Please use a different email address.",
        );
      } else {
        setApiError(error);
      }
      trackError(AnalyticsEvent.loginGetOTPError, error);
      return;
    } finally {
      setIsInProgress(false);
    }
  };

  return {
    emailInputRef,
    errors,
    apiError,
    isInProgress,
    email,
    setEmail,
    setApiError,
    handleEmailSubmit,
    checkEmailValidation,
    isSubmittedOnce,
  };
};

export const useOtpState = (
  phone: string,
  email: string,
  id: string,
  callback: OtpComponentCallback,
) => {
  const [apiError, setApiError] = useState<any>("");
  const [errors, setErrors] = useState<any[]>([]);
  const [isSubmittedOnce, seIsSubmittedOnce] = useState<boolean>(false);
  const { track, trackError } = useAnalytics();
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [verificationId, setVerificationId] = useState<string>(id);
  const [otpInputArray, setOtpInputArray] = useState<string[]>(
    Array(6).fill(""),
  );

  const otpInputRef1 = useRef<HTMLInputElement>(null);
  const otpInputRef2 = useRef<HTMLInputElement>(null);
  const otpInputRef3 = useRef<HTMLInputElement>(null);
  const otpInputRef4 = useRef<HTMLInputElement>(null);
  const otpInputRef5 = useRef<HTMLInputElement>(null);
  const otpInputRef6 = useRef<HTMLInputElement>(null);

  const otpInputRefs = [
    otpInputRef1,
    otpInputRef2,
    otpInputRef3,
    otpInputRef4,
    otpInputRef5,
    otpInputRef6,
  ];

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtpArray = [...otpInputArray];
    newOtpArray[index] = value;
    setOtpInputArray(newOtpArray);

    const newOtpString = newOtpArray.join("");
    setOtp(newOtpString);
    checkOtpValidation(newOtpString);

    if (value && index < 5) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otpInputArray[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };

  useEffect(() => {
    otpInputRefs[0].current?.focus();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const checkOtpValidation = (otp: string) => {
    const otpSchema = z.object({
      otp: z
        .number({
          required_error: REQUIRED_ERROR_OTP,
          invalid_type_error: INVALID_OTP,
        })
        .positive(),
      otp_length: z.string().length(6, { message: VALIDATION_ERROR_OTP }),
    });

    const response = otpSchema.safeParse({
      otp: parseInt(otp.trim()),
      otp_length: otp,
    });

    if (!response.success) {
      const errArr: any[] = [];
      const { errors: err } = response.error;
      for (let i = 0; i < err.length; i++) {
        errArr.push({ for: err[i].path[0], message: err[i].message });
      }
      setErrors(errArr);
      setIsInProgress(false);
      return errArr;
    }
    setErrors([]);
    return [];
  };

  const handleOtpSubmit = async (e: any) => {
    e.preventDefault();
    setIsInProgress(true);
    seIsSubmittedOnce(true);
    track(AnalyticsEvent.loginVerifyOtpTap);
    const errors = checkOtpValidation(otp);
    if (errors.length > 0) return;

    try {
      let token: string;
      let user: DbUser;

      if (email) {
        token = await authService.verifyOtp2(
          verificationId,
          otp,
          "email",
          undefined,
          email,
        );
        const userCredential = await signInWithCustomToken(firebaseAuth, token);
        user = await authService.loginWithEmail(userCredential.user.uid, email);
      } else {
        token = await authService.verifyOtp2(
          verificationId,
          otp,
          "phone",
          phone,
          undefined,
        );
        const userCredential = await signInWithCustomToken(firebaseAuth, token);
        user = await authService.loginWithPhone(userCredential.user.uid, phone);
      }

      const deviceData = await fetchDeviceInfoAndFcmToken();
      if (deviceData) {
        const sessionId = await userService.createUserSession(
          user.id,
          deviceData.deviceInfo,
          deviceData.fcmToken,
        );
        setCookie("session_id", sessionId);
      }
      callback.onSuccess(user);
    } catch (error: any) {
      if (error.response && error.response.data == INVALID_OTP) {
        setApiError(ERROR_MESSAGE_OTP);
      } else {
        setApiError(error);
      }
      trackError(AnalyticsEvent.loginVerifyOtpError, error);
      setIsInProgress(false);
    }
  };

  const resendOtp = async () => {
    try {
      const token = await executeRecaptcha();
      let newVerificationId: string;
      if (email) {
        newVerificationId = await authService.sendOtpEmail(email, token);
      } else {
        newVerificationId = await authService.sendOtp(phone, token);
      }
      setVerificationId(newVerificationId);
      setOtp("");
      setOtpInputArray(Array(6).fill(""));
      setTimer(30);
    } catch (error: any) {
      if (
        error.response?.status === 400 &&
        error.response?.data === "email-already-exists"
      ) {
        setApiError(
          "This email is already registered. Please use a different email address.",
        );
      } else if (
        error.response?.status === 404 &&
        error.response?.data === "USER_NOT_FOUND_USE_EMAIL"
      ) {
        setApiError(ERROR_MESSAGE_USER_NOT_FOUND_USE_EMAIL);
      } else {
        setApiError(error);
      }
    }
  };

  const [timer, setTimer] = useState(30);

  useEffect(() => {
    timer > 0 &&
      setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
  }, [timer]);

  return {
    errors,
    apiError,
    isInProgress,
    otp,
    setApiError,
    handleOtpSubmit,
    isSubmittedOnce,
    resendOtp,
    timer,
    otpInputArray,
    otpInputRefs,
    handleChange,
    handleKeyDown,
  };
};

export type {
  ProfileComponentCallback,
  OtpComponentCallback,
  PhoneComponentCallback,
  EmailComponentCallback,
} from "./hooks/utils";
